import { GetPopUpResponse, PopUpAction, PopUpCustomization } from '../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { isDefined, isEmpty, kilometersOrMiles, undefinedIfEmpty } from '../helpers/utils';

import { FormInputButton } from './theme/form-input-button';
import { StateController } from '../helpers/state/state-management/state-controller';
import { addEmailCapture } from '../helpers/api/email-capture-api';
import { calculateDistance } from '../helpers/standalone-popups-display';
import { getMainPage } from '../helpers/page-name';
import { metersToMiles } from '../helpers/shapes';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';
import { trackPopupEvent } from '../helpers/api/popups-api';
import { validateEmail } from '../helpers/strings';

@customElement('email-capture-modal')
export class EmailCaptureModal extends LitElement {
    private readonly amplitude_callsite: string = 'Email Capture Modal';

    @query('#emailAddress')
    private readonly emailAddressElement?: FormInputButton;

    @property({ type: Object })
    popUp?: PopUpCustomization | GetPopUpResponse;

    @state()
    canCaptureEmail: boolean = true;

    @state()
    popupTrackingId?: string;

    @state()
    ip_address: string = '';

    @state()
    email_address?: string = '';

    @property({ type: Number })
    userLat?: number;

    @property({ type: Number })
    userLng?: number;

    private readonly bindTopicState = new StateController(this, topicData);

    async connectedCallback() {
        super.connectedCallback();

        try {
          const { ip } = await fetch('https://api64.ipify.org?format=json').then(async (response) => await response.json());
          this.ip_address = ip;
        } catch (error) {
          console.error('Failed to fetch IP address:', error);
        }
    }

    protected async firstUpdated() {
        await this.startup();
    }

    startup() {
        document.documentElement.style.setProperty('--mdc-theme-surface', this.popUp?.background_color ?? '#FFF');

        this.style.setProperty('--mdc-theme-surface', this.popUp?.background_color ?? '#FFF');
    }

    async clickCTA() {
        // Check if this is a new standalone popup or old embedded popup
        const isStandalonePopup = 'action' in (this.popUp ?? {});

        if (isStandalonePopup && isDefined(this.popUp)) {
            const popup = this.popUp as GetPopUpResponse;
            // Guard against missing _id (e.g., in preview mode)
            const popupId = popup._id?.$oid;
            if (isDefined(popupId)) {
                const closedInHandler = await this.handlePopupAction(popup.action, popupId, topicData.topic?._id?.$oid);
                if (!closedInHandler) {
                    await this.closeModal();
                }
            } else {
                // Preview mode or missing ID - just close without tracking
                await this.closeModal();
            }
        } else if (isDefined(this.popUp)) {
            // Old popup format
            const popup = this.popUp as PopUpCustomization;
            if (popup.collect_email ?? false) {
                await this.submitEmail(false);
            }
            if (popup.cta_link) {
                window.open(popup.cta_link, '_blank');
            }
            await this.closeModal();
        }
    }

    private async trackClick(popupId: string, topicId?: string) {
        if (topicId) {
            try {
                await trackPopupEvent(popupId, topicId, 'click');
            } catch (err) {
                console.error('Failed to track popup click event:', err);
            }
        }
    }

    private async handlePopupAction(action: PopUpAction, popupId: string, topicId?: string): Promise<boolean> {
        if (action.type === 'none') {
            // Just dismiss — no click to track
            return false;
        }

        if (action.type === 'collect-email') {
            // submitEmail handles its own success/failure — only track click if email was valid
            const rawEmail = undefinedIfEmpty(this.email_address?.trim()?.toLowerCase()) ?? this.emailAddressElement?.value?.trim()?.toLowerCase();
            const email = rawEmail ?? '';
            if (validateEmail(email)) {
                await this.trackClick(popupId, topicId);
            }
            await this.submitEmail(true);
            return true; // Modal was closed in submitEmail
        }

        // Track click for successful actions
        await this.trackClick(popupId, topicId);

        if (action.type === 'link-out') {
            window.open(action.data.url, '_blank');
            return false;
        }

        if (action.type === 'open-point') {
            // Dispatch event to open a specific point on the map
            this.dispatchEvent(new CustomEvent('open-point', {
                detail: { pointId: action.data.point_id.$oid },
                bubbles: true,
                composed: true,
            }));
            return false;
        }

        return false;
    }

    private async submitEmail(closeModal: boolean = true) {
         const rawEmail = undefinedIfEmpty(this.email_address?.trim()?.toLowerCase()) ?? this.emailAddressElement?.value?.trim()?.toLowerCase();
         const email = rawEmail ?? '';
         const emailValid = validateEmail(email);
        if (emailValid && isDefined(this.popUp)) {
            // Determine popup type and get appropriate fields
            const isStandalonePopup = 'action' in (this.popUp ?? {});
            let title = '';
            let description = '';
            let cta = '';

            if (isStandalonePopup) {
                const popup = this.popUp as GetPopUpResponse;
                title = popup.header ?? '';
                description = popup.description ?? '';
                cta = 'Submit';
            } else {
                const popup = this.popUp as PopUpCustomization;
                title = popup.pop_up_header ?? '';
                description = popup.pop_up_description ?? '';
                cta = popup.cta_button_text ?? '';
            }

            const emailCaptureRequest = {
                email,
                ip_address: this.ip_address ?? '',
                text: { title, description, cta },
                ...(isStandalonePopup && '_id' in this.popUp ? { pop_up_id: this.popUp._id } : {})
            };
            try{
                await addEmailCapture(this.amplitude_callsite, topicData.topicId, emailCaptureRequest);
            } catch (err) {
                console.error('Failed to add email capture:', err);
                getMainPage().errorToastElement.showToastAndThrow('An error occurred while submitting your email. Please try again later.');
                return;
            }

            if (closeModal) {
                await this.closeModal();
            }
        } else {
            console.warn('Invalid email format', email);
            getMainPage().errorToastElement.showToastAndThrow('Invalid email format.');
        }
    }

    async dismiss() {
        await this.closeModal(true);
    }

    private async closeModal(isDismiss: boolean = false) {
        const isStandalonePopup = 'action' in (this.popUp ?? {});
        let onlyShowOnce = false;
        let modalId = '';

        if (isStandalonePopup && isDefined(this.popUp)) {
            const popup = this.popUp as GetPopUpResponse;
            const popupId = popup._id?.$oid;
            // Only track dismiss for explicit close actions (close button/backdrop),
            // not when closing after a CTA click or email submission
            const topicId = topicData.topic?._id?.$oid;
            if (isDismiss && isDefined(popupId) && isDefined(topicId)) {
                await trackPopupEvent(popupId, topicId, 'dismiss');
            }
            modalId = popupId ?? '';
        } else if (isDefined(this.popUp)) {
            const popup = this.popUp as PopUpCustomization;
            modalId = popup._id?.$oid ?? '';
            onlyShowOnce = popup.display_once ?? false;
        }

        this.dispatchEvent(new CustomEvent('close-modal', {
            detail: { onlyShowOnce, modal_id: modalId }
        }));
    }

    handleChangeEmailAddress(e: any) {
        this.email_address = e.detail.value;
    }

    render() {
        if (!isDefined(this.popUp)) {
            return html``;
        }

        const isStandalonePopup = 'action' in (this.popUp ?? {});
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        const headerContent = 'pop_up_header' in (this.popUp ?? {}) ? (this.popUp as any).pop_up_header : (this.popUp as GetPopUpResponse).header;
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        const descriptionContent = 'pop_up_description' in (this.popUp ?? {}) ? (this.popUp as any).pop_up_description : (this.popUp as GetPopUpResponse).description;
        return html`
            <div class="add-modal__wrapper" role="document" aria-label="${headerContent ?? 'Notification'}" style=${`background-color: ${String(this.popUp.background_color)}; color: ${String(this.popUp.text_color)}`}>
                <h1 class="add-modal__title" style="color:${String(this.popUp?.text_color)}">
                    ${headerContent}
                </h1>
                ${this.renderSubheaderInfo()}
                <p class="add-modal__description" style="color:${String(this.popUp?.text_color)}">
                    ${descriptionContent}
                </p>
                ${this.renderPointActions()}
                ${this.renderActionContent(isStandalonePopup)}
                <error-toast id="errorToast"></error-toast>
            </div>
        `;
    }

    private renderSubheaderInfo() {
        if (!isDefined(this.popUp) || !('action' in this.popUp)) return '';
        const popup = this.popUp ;
        const parts: Array<ReturnType<typeof html> | string> = [];

        // Distance (localized: metric or imperial)
        if (popup.show_distance_to_user && isDefined(popup.geo_trigger) && this.userLat != null && this.userLng != null) {
            const distanceMeters = calculateDistance(
                this.userLat, this.userLng,
                popup.geo_trigger.lat, popup.geo_trigger.lng
            );
            const useImperial = kilometersOrMiles() === 'mi';
            let distanceText: string;
            if (useImperial) {
                const miles = metersToMiles(distanceMeters);
                const feet = Math.round(distanceMeters * 3.28084);
                distanceText = miles < 0.1 ? `${feet} ft away` : `${miles.toFixed(1)} mi away`;
            } else {
                distanceText = distanceMeters < 1000
                    ? `${Math.round(distanceMeters)}m away`
                    : `${(distanceMeters / 1000).toFixed(1)} km away`;
            }
            parts.push(html`<span>${distanceText}</span>`);
        }

        // Rating
        if (isDefined(popup.show_rating_field_id) && popup.action.type === 'open-point') {
            const pointId = popup.action.data.point_id.$oid;
            const point = pointCache.orderedPoints.find(p => p._id?.$oid === pointId);
            if (point) {
                const customFields = topicData.topic?.add_point_customization?.custom_fields;
                const ratingField = customFields?.find(f => f._id?.$oid === popup.show_rating_field_id?.$oid);
                if (ratingField && 'Rating' in ratingField.field_type) {
                    const entry = point.custom_information?.custom_field_entries?.find(
                        e => e.field_id.$oid === ratingField._id?.$oid
                    );
                    if (entry?.field_value) {
                        const maxStars = (ratingField.field_type as { Rating: { maxStars?: number } }).Rating?.maxStars ?? 5;
                        const value = parseInt(entry.field_value);
                        parts.push(html`<span style="color: #FFB400; letter-spacing: 2px;">${'\u2605'.repeat(Math.min(value, maxStars))}${'\u2606'.repeat(Math.max(0, maxStars - value))}</span>`);
                    }
                }
            }
        }

        if (parts.length === 0) return '';

        return html`
            <div style="display: flex; align-items: center; gap: 8px; opacity: 0.75; font-size: 0.9em; margin: 4px 0 8px; color: ${popup.text_color};">
                ${parts.map((part, i) => html`${i > 0 ? html`<span style="opacity: 0.5;">|</span>` : ''}${part}`)}
            </div>
        `;
    }

    private renderPointActions() {
        if (!isDefined(this.popUp) || !('show_point_actions' in this.popUp)) return '';
        const popup = this.popUp ;
        if (!popup.show_point_actions || popup.action.type !== 'open-point') return '';

        const pointId = popup.action.data.point_id.$oid;
        const point = pointCache.orderedPoints.find(p => p._id?.$oid === pointId);
        if (!point) return '';

        return html`
            <point-card-actions
                .point=${point}
                .hideCreateMap=${true}
                .centered=${true}
                style="margin-top: 8px;"
            ></point-card-actions>
        `;
    }

    private renderActionContent(isStandalonePopup: boolean) {
        if (!isDefined(this.popUp)) return '';

        if (isStandalonePopup) {
            const popup = this.popUp as GetPopUpResponse;
            const action = popup.action;

            // None action: info only
            if (action.type === 'none') {
                return '';
            }

            // CollectEmail action
            if (action.type === 'collect-email') {
                return html`
                    ${undefinedIfEmpty(action.data.opt_in_language) ? html`
                        <div class="inline-element">
                            <mwc-formfield label="">
                                <mwc-checkbox
                                    id="canCaptureEmail"
                                    style="--mdc-checkbox-checked-color: ${this.popUp?.text_color};--mdc-checkbox-ink-color: ${this.popUp?.background_color};"
                                    ?checked=${this.canCaptureEmail}
                                    @click=${() => {this.canCaptureEmail = !this.canCaptureEmail;}}
                                ></mwc-checkbox>
                            </mwc-formfield>
                            <span>${action.data.opt_in_language} | <a href="https://www.proxi.co/d/privacy-policy" target="_blank" style="color:${this.popUp?.text_color}">
                                Privacy Policy.</a></span>
                        </div>
                    ` : ''}
                    <div class="email-wrapper">
                        <form-input-button
                            id="emailAddress"
                            style="width:100%"
                            readonly="false"
                            buttonBackgroundColor="${popup.cta_background_color}"
                            buttonTextColor="${popup.cta_text_color}"
                            placeholder="Your email"
                            @submit="${this.clickCTA}"
                        >Submit
                        </form-input-button>
                    </div>
                `;
            }

            // LinkOut action
            if (action.type === 'link-out') {
                return html`
                    <form-button
                        variant="heavy"
                        size="lg"
                        fullWidth
                        style="width:100%"
                        class="submit-button"
                        backgroundColor="${popup.cta_background_color}"
                        textColor="${popup.cta_text_color}"
                        @click="${this.clickCTA}"
                    >
                        ${action.data.button_text}
                    </form-button>
                `;
            }

            // OpenPoint action
            if (action.type === 'open-point') {
                return html`
                    <form-button
                        class="submit-button"
                        variant="heavy"
                        size="lg"
                        fullWidth
                        style="width:100%"
                        backgroundColor="${popup.cta_background_color}"
                        textColor="${popup.cta_text_color}"
                        @click="${this.clickCTA}"
                    >
                        ${action.data.button_text}
                    </form-button>
                `;
            }
        } else {
            // Old popup format
            const popup = this.popUp as PopUpCustomization;
            return html`
                ${popup.collect_email ?? true ? html`
                    <div class="inline-element">
                        <mwc-formfield label="">
                            <mwc-checkbox
                                id="canCaptureEmail"
                                ?checked=${this.canCaptureEmail}
                                @click=${() => {this.canCaptureEmail = !this.canCaptureEmail;}}
                            ></mwc-checkbox>
                        </mwc-formfield>
                        <span>${popup.opt_in_language} | <a href="https://www.proxi.co/d/privacy-policy" target="_blank" style="color:${this.popUp?.text_color}">
                            Privacy Policy.</a></span>
                    </div>
                ` : ''}
                ${popup.collect_email ?? true ? html`
                    <div class="email-wrapper">
                        <form-input
                            id="emailAddress"
                            class="email-input"
                            type="email"
                            placeholder="Your email"
                            .value="${this.email_address ?? ''}"
                            @change="${this.handleChangeEmailAddress}"
                            ?noborder=${true}
                        ></form-input>
                        ${popup.cta_link ? '' : html`
                            <button
                                class="submit-button"
                                .disabled=${!this.canCaptureEmail || isEmpty(this.email_address)}
                                @click="${this.submitEmail}"
                                style="${this.canCaptureEmail ? `${`background-color: ${String(this.popUp?.cta_background_color)}; color: ${String(this.popUp?.cta_text_color)}`}` : `background-color: ${String(this.popUp?.cta_background_color)}`}"
                            >
                                Submit
                            </button>
                        `}
                    </div>
                ` : ''}
                ${popup.cta_link ? html`
                    <button
                        size="md"
                        class="submit-button"
                        .disabled=${(!this.canCaptureEmail || isEmpty(this.email_address)) && popup.collect_email === true}
                        @click="${this.clickCTA}"
                        style="${`background-color: ${String(this.popUp?.cta_background_color)}; color: ${String(this.popUp?.cta_text_color)}`}"
                    >
                        ${popup.cta_button_text}
                    </button>
                ` : ''}
            `;
        }
    }

    static get styles() {
        return css`
            .add-modal__wrapper {
                display: flex;
                padding: 0.5rem 1.5rem 1rem;
                flex-direction: column;
                align-items: center;
                gap: 12px;
                height: 100%;
            }

            .add-modal__logo {
                max-width: 100%;
                max-height: 120px;
                object-fit: contain;
                border-radius: 4px;
            }

            h1.add-modal__title {
                color: #091E2C;
                text-align: center;
                font-size: 1rem;
                font-weight: 700;
                line-height: 130%;
                margin: 0;
            }

            p.add-modal__description {
                color: #666276;
                text-align: center;
                font-size: 0.8rem;
                line-height: 150%;
                margin: 0;
            }

            .email-wrapper {
                display: flex;
                padding: 4px;
                width: 100%;
                justify-content: flex-end;
                align-items: center;
                border-radius: 10px;
                border: 1px solid #EFECF3;
                background: #FFF;
                box-shadow: 0px 4px 12px 0px rgba(31, 27, 45, 0.08), 0px 2px 2px -2px rgba(31, 27, 45, 0.08);
                gap: 8px;
            }

            .email-input {
                flex: 1;
            }

            .submit-button {
                cursor: pointer;
                border-radius: 10px;
                font-weight: bold;
                padding: 10px 15px;
                border: none;
            }

            #emailCaptureDrawer {
                --bottom-height: 0px;
                --bottom-height-half: 0px;
            }

            .inline-element {
                display: inline-flex;
                align-items: center;
                font-size: 0.75rem;
                line-height: 1rem;
            }

            p.privacy-policy {
                color: #666276;
                line-height: 150%;
                margin: 0;
                text-align: center;
            }

            @media only screen and (max-width: 550px) {

                h1.add-modal__title {
                    color: #454056;
                    font-size: 1rem;
                }

                .email-wrapper {
                    width: 100%;
                    padding: 0;
                    flex-direction: column;
                    justify-content: flex-start;
                    align-items: center;
                    border-radius: 0;
                    border: none;
                    background: transparent;
                    box-shadow: none;
                    gap: 12px;
                }

                .email-input {
                    width: 100%;
                }

                .submit-button {
                    cursor: pointer;
                    border-radius: 4px;
                    padding: 14px 32px;
                    border: none;
                    color: #102C3D;
                    text-align: center;
                    font-size: 12px;
                    line-height: 150%;
                }
            }
        `;
    }
}
